import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Container, useMediaQuery, Box, useTheme, Grid } from '@mui/material';
import {
  SearchBar,
  Footer,
  PreviewHeader,
  CharacterList,
  MobileNav,
  CustomButton,
  Pagination,
  Banner,
  useHomepageAPI,
  AuthenticationModal,
  TopLoginSection,
  Sidebar,
} from '@client/components';
import { useUserStore } from '@client/store';
import { Utils } from '@client/utility';

export function Home() {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { 
    userUUID, 
    is_login, 
    include_sensitive, 
    enableNSFW, 
    disableNSFW,
    setIncludeSensitive,
    setDate
  } = useUserStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const {
    characters,
    loading,
    totalPages,
    t,
    currentPage,
    handleCharacterFetchClick,
    onButtonTagClicked,
    handleSearch,
    setPage,
    authLoading,
    initialLoaded,
    refetchCharacters,
  } = useHomepageAPI();

  useEffect(() => {
    const fetchUserSensitiveSetting = async () => {
      if (!userUUID) {
        setIncludeSensitive(false);
        setDate(null);
        return;
      }
      try {
        const result = await Utils.db.select<{
          include_sensitive?: boolean;
          date?: string;
        }>({
          tables: 'user_data',
          columns: 'include_sensitive, date',
          match: { user_uuid: userUUID },
          paging: false,
          countOption: null,
        });
        const userData = result.data?.[0];
        const sensitive = userData?.include_sensitive ?? false;
        const date = userData?.date ?? null;
        
        setIncludeSensitive(sensitive);
        setDate(date);
      } catch (err) {
        setIncludeSensitive(false);
        setDate(null);
      }
    };
    
    fetchUserSensitiveSetting();
  }, [userUUID]);

  useEffect(() => {
    if (!searchParams.get('page')) {
      setSearchParams(
        { ...Object.fromEntries(searchParams.entries()), page: '1' },
        { replace: true }
      );
      setPage(1);
    }
  }, [searchParams, setSearchParams, setPage]);

  const onSearch = (query: string) => {
    setSearchParams(
      {
        ...Object.fromEntries(searchParams.entries()),
        search: query,
        page: '1',
      },
      { replace: true }
    );
    handleSearch(query);
  };

  const handleNSFWChange = useCallback(async (enabled: boolean) => {
    if (!userUUID) {
      throw new Error('User not logged in');
    }
    
    const date = enabled ? new Date().toISOString() : null;
    
    if (enabled) {
      enableNSFW(date);
    } else {
      disableNSFW();
    }
    
    try {
      await Utils.db.update({
        tables: 'user_data',
        values: { 
          include_sensitive: enabled,
          date: date
        },
        match: { user_uuid: userUUID },
      });
      
      await refetchCharacters();
      
    } catch (err) {
      if (enabled) {
        disableNSFW();
      } else {
        enableNSFW(new Date().toISOString());
      }
      throw err;
    }
  }, [userUUID, enableNSFW, disableNSFW, refetchCharacters]);

  const toggleMode = () => {
    setShowLogin(!showLogin);
    setShowRegister(!showRegister);
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
      }}
    >
      <TopLoginSection
        setShowLogin={setShowLogin}
        setShowRegister={setShowRegister}
      />

      <motion.div
        className="flex flex-col min-h-screen text-white"
        aria-label={t('ariaLabels.homePage')}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box component="header" sx={{ width: '100%' }}>
          <PreviewHeader />
        </Box>

        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {!authLoading && is_login && !isMobile && (
            <Box
              component="nav"
              sx={{ display: { xs: 'none', md: 'flex' }, mt: 2 }}
              aria-label={t('ariaLabels.mainNavigation')}
            >
              <Sidebar />
            </Box>
          )}

          <Grid
            container
            component="main"
            sx={{
              flex: 1,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Grid size={12}>
              <Banner />
            </Grid>

            <Grid
              size={{ xs: 12, md: 10, lg: 8 }}
              sx={{
                mb: 6,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
              }}
            >
              <h2 id="search-heading" className="sr-only">
                {t('ariaLabels.searchCharacters')}
              </h2>
              <SearchBar 
                onSearch={onSearch} 
                defaultValue={searchQuery}
                showNSFW={include_sensitive}
                onNSFWChange={handleNSFWChange}
                userUUID={userUUID}
              />

              <h2 id="custom-action-heading" className="sr-only">
                {t('ariaLabels.customActionButton')}
              </h2>
              <CustomButton
                onButtonClick={handleCharacterFetchClick}
                onButtonTagClicked={onButtonTagClicked}
              />
            </Grid>

            <Grid size={12} sx={{ width: '100%' }}>
              <h2 id="characters-heading" className="sr-only">
                {t('ariaLabels.characterList')}
              </h2>
              <CharacterList
                characters={characters}
                loading={loading}
                itemsPerPage={20}
                t={t}
                initialLoaded={initialLoaded}
              />
            </Grid>

            <Grid size={12}>
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onNext={() => setPage(currentPage + 1)}
                onPrev={() => setPage(currentPage - 1)}
              />
            </Grid>
          </Grid>
        </Container>

        {isMobile && <MobileNav setShowLoginModal={setShowLogin} />}

        <Box component="footer" sx={{ px: 4 }} role="contentinfo">
          <Footer />
        </Box>
      </motion.div>

      {(showLogin || showRegister) && (
        <AuthenticationModal
          mode={showLogin ? 'login' : 'register'}
          onClose={() => {
            setShowLogin(false);
            setShowRegister(false);
          }}
          toggleMode={toggleMode}
        />
      )}
    </Box>
  );
}